import {
  Box,
  Button,
  ChakraStyledOptions,
  Flex,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Table,
  Tbody,
  Td,
  Th,
  Thead,
  Tooltip,
  Tr,
} from '@chakra-ui/react';
import React from 'react';

import {
  date as commonDate,
  utils as commonUtils,
} from '@finley-technologies/common';

import {
  InvoiceConsolidatedDisbursementFragmentGQLType,
  InvoiceConsolidatedDisbursementStatusGQLType,
  InvoiceReportGQLType,
  InvoiceSummaryFragmentGQLType,
} from 'types/dashboard-core-gqlTypes.generated';

import { Typography } from 'packages/common-frontend/components/Typography';
import { colors } from '~/packages/common-frontend/colors';
import { Icon } from '~/packages/common-frontend/components/icons';
import {
  dollarNumberWithCommas,
  percentageToPercentageLabel,
} from '~/packages/common-frontend/utils';
import {
  MarkAsDisbursedButton,
  UndoMarkAsDisbursedButton,
} from '~/packages/invoices/ui/consolidatedDisbursements/MarkAsDisbursedButton';
import { DisbursementNoticeDownloadButton } from '~/packages/invoices/ui/disbursements/InvoiceDisbursementDownloadButton';
import { DownloadAllLiveDisbursementNoticesButton } from '~/packages/loans/ui/DownloadAllLiveDisbursementNoticesButton';

const TABLE_CELL_STYLES = {
  backgroundColor: 'white',
  color: colors.gray[500],
  whiteSpace: 'normal',
};

const HeaderCell = ({
  styles,
  children,
}: {
  styles?: ChakraStyledOptions;
  children: React.ReactNode;
}) => {
  return (
    <Th fontWeight={500} fontSize="14px" color="black" {...styles}>
      {children}
    </Th>
  );
};

export const MarkDisbursedMenu = ({
  disbursement,
  invoice,
}: {
  disbursement: InvoiceConsolidatedDisbursementFragmentGQLType;
  invoice: InvoiceSummaryFragmentGQLType;
}) => {
  const paymentAction =
    disbursement.totalCashDisbursementAmount > 0 ? 'Disbursed' : 'Notified';

  const actionMap: Record<
    InvoiceConsolidatedDisbursementStatusGQLType,
    string
  > = {
    paid: paymentAction,
    canceled: '-',
    created: `Mark ${paymentAction}`,
  };

  if (disbursement.status === 'created') {
    return (
      <MarkAsDisbursedButton
        invoiceConsolidatedDisbursement={disbursement}
        paymentAction={paymentAction}
        invoice={invoice}
      />
    );
  }

  return (
    <Menu>
      <MenuButton
        fontSize="14px"
        fontWeight="500"
        w="full"
        disabled={disbursement.status !== 'paid'}
      >
        <Flex alignItems="center">
          <Typography
            fontWeight="semibold"
            colorScheme="v2-aurora"
            TextProps={{ textAlign: 'left', mr: '2' }}
          >
            {actionMap[disbursement.status]}
          </Typography>
          {disbursement.status === 'paid' && <Icon icon="chevron-down" />}
        </Flex>
      </MenuButton>

      <MenuList>
        <UndoMarkAsDisbursedButton
          invoiceConsolidatedDisbursementId={disbursement.id}
          paymentAction={paymentAction}
        />
      </MenuList>
    </Menu>
  );
};

const InvoiceConsolidatedDisbursementRow = ({
  disbursement,
  invoiceTotalAmount,
  hideBorder,
  reportDownload,
  invoice,
}: {
  disbursement: InvoiceConsolidatedDisbursementFragmentGQLType;
  invoiceTotalAmount: number;
  hideBorder: boolean;
  reportDownload: InvoiceReportGQLType | undefined;
  invoice: InvoiceSummaryFragmentGQLType;
}) => {
  return (
    <Tr key={disbursement.id}>
      <Td sx={TABLE_CELL_STYLES} border={hideBorder ? 'none' : undefined}>
        <MarkDisbursedMenu disbursement={disbursement} invoice={invoice} />
      </Td>
      <Td
        whiteSpace="nowrap"
        sx={{ fontWeight: 500, color: 'black' }}
        border={hideBorder ? 'none' : undefined}
      >
        {disbursement.capitalProviderOrganization.name}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
          textAlign: 'right',
        }}
      >
        {percentageToPercentageLabel(
          commonUtils.divideByOrZero(
            disbursement.totalCashDisbursementAmount +
              disbursement.totalInterestCapitalizationAmount,
            invoiceTotalAmount,
          ) * 100,
          6,
          true,
        )}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(disbursement.totalCashDisbursementAmount)}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(disbursement.totalCashServiceFeeAmount)}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(
          disbursement.totalPostServiceFeeCashDisbursementAmount,
        )}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(
          disbursement.totalInterestCapitalizationAmount,
        ) ?? '-'}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(
          disbursement.totalInterestCapitalizationServiceFeeAmount,
        )}
      </Td>
      <Td
        textAlign="right"
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {dollarNumberWithCommas(
          disbursement.totalPostServiceFeeInterestCapitalizationAmount,
        )}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
        }}
      >
        {disbursement.paymentDate
          ? commonDate.fullDateStrToHumanDateString(disbursement.paymentDate)
          : '-'}
      </Td>
      <Td
        sx={{
          ...TABLE_CELL_STYLES,
          border: hideBorder ? 'none' : undefined,
          textAlign: 'center',
        }}
      >
        {reportDownload ? (
          <DisbursementNoticeDownloadButton report={reportDownload} />
        ) : (
          <Tooltip label="No Disbursement Notice Found">
            <Button variant="v2-ghost" isDisabled>
              <Icon icon="arrow-down-to-bracket" />
            </Button>
          </Tooltip>
        )}
      </Td>
    </Tr>
  );
};

export const InvoiceConsolidatedDisbursementsTable = ({
  consolidatedDisbursements,
  invoiceReports,
  invoice,
}: {
  consolidatedDisbursements: InvoiceConsolidatedDisbursementFragmentGQLType[];
  invoiceReports?: InvoiceReportGQLType[];
  invoice: InvoiceSummaryFragmentGQLType;
}) => {
  return (
    <Box w="100%" overflowX="auto">
      <Table>
        <Thead>
          <Tr>
            <HeaderCell styles={{ width: '15%' }}>Disbursed</HeaderCell>
            <HeaderCell styles={{ width: '20%' }}>Lender</HeaderCell>
            <HeaderCell
              styles={{
                textAlign: 'right',
                width: '16%',
              }}
            >
              Lender Share
            </HeaderCell>
            <HeaderCell styles={{ width: '14%', textAlign: 'right' }}>
              Gross Disbursement Amount
            </HeaderCell>
            <HeaderCell styles={{ width: '14%', textAlign: 'right' }}>
              Disbursement Servicer Fee
            </HeaderCell>
            <HeaderCell styles={{ width: '14%', textAlign: 'right' }}>
              Net Disbursement Amount
            </HeaderCell>
            <HeaderCell styles={{ width: '10%', textAlign: 'right' }}>
              Gross Capitalization Amount
            </HeaderCell>
            <HeaderCell styles={{ width: '10%', textAlign: 'right' }}>
              Capitalization Servicer Fee
            </HeaderCell>
            <HeaderCell styles={{ width: '10%', textAlign: 'right' }}>
              Net Capitalization Amount
            </HeaderCell>
            <HeaderCell styles={{ width: '10%' }}>Disbursement Date</HeaderCell>
            <Th
              fontWeight={500}
              fontSize="14px"
              color="black"
              width="5%"
              textAlign="center"
            >
              <Menu>
                <MenuButton
                  fontSize="inherit"
                  fontWeight="inherit"
                  aria-label="Download and email all funding requests"
                >
                  <HStack>
                    <span>Notice</span>
                    <Icon icon="chevron-down" />
                  </HStack>
                </MenuButton>
                <MenuList>
                  <MenuItem as="div">
                    <DownloadAllLiveDisbursementNoticesButton
                      invoiceReports={invoiceReports}
                    />
                  </MenuItem>
                </MenuList>
              </Menu>
            </Th>
          </Tr>
        </Thead>
        <Tbody>
          {consolidatedDisbursements.map((disbursement, idx) => {
            // hide the bottom border on the last row
            const hideBorder = idx === consolidatedDisbursements.length - 1;
            const reportDownload = invoiceReports?.find(
              (download) =>
                download.capitalProviderId ===
                  disbursement.capitalProviderOrganization.id &&
                download.reportType === 'disbursementNotice',
            );

            return (
              <InvoiceConsolidatedDisbursementRow
                key={disbursement.id}
                disbursement={disbursement}
                invoiceTotalAmount={invoice.totalAmount}
                hideBorder={hideBorder}
                reportDownload={reportDownload}
                invoice={invoice}
              />
            );
          })}
        </Tbody>
      </Table>
    </Box>
  );
};
